import { ReactNode, useRef } from "react";

import {
  Collapse,
  FormHelperText,
  Autocomplete,
  TextField,
  Box,
  Typography,
} from "@mui/material";

import { ReactComponent as IconChevronDown } from "assets/svg/chevron_down.svg";

import pxToRem from "theme/base/pxToRem";

import { get } from "lodash";

import { Label, Required, Wrapper } from "./style";

interface SelectOption {
  value: string | number;
  label: string;
}

interface SelectProps {
  // biến này lưu thông tin value của select
  name: string;
  // biến này lưu thông tin label của select, dùng để xử lý 1 vài trường hợp đặc biệt khi data cần lấy label của select
  labelName?: string;
  formData: object;
  formErrors?: object;
  label?: string;
  labelColor?: string;
  options: SelectOption[];
  labelIcon?: ReactNode;
  attachRef?: Function;
  onChange?: Function;
  onKeyDown?: Function;
  selectProps?: object;
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
  multiple?: boolean;
  fontSize?: number;
  onInputChange?: (value) => void;
}

const FormSelect = ({
  name,
  formData,
  labelName,
  label,
  formErrors,
  options,
  labelIcon,
  labelColor,
  attachRef,
  onChange,
  onKeyDown,
  selectProps,
  disabled,
  placeholder,
  required = false,
  multiple = false,
  fontSize = 16,
  onInputChange,
}: SelectProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  return (
    <Wrapper
      variant="standard"
      fullWidth
      sx={{
        li: {
          padding: pxToRem(16),
        },
      }}
      hasLabel={!!label}
    >
      {!!label && (
        <Label
          disabled={disabled}
          shrink
          htmlFor={name}
          variant="standard"
          sx={(theme) => ({
            color:
              theme.palette.text?.[labelColor] ||
              theme.palette.text.textPrimary,
            fontSize: pxToRem(fontSize),
          })}
        >
          {labelIcon} {label}
          {required && <Required>*</Required>}
        </Label>
      )}
      <Autocomplete
        className="MuiSelect-select"
        id={name}
        options={options}
        multiple={multiple}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        value={
          multiple
            ? get(formData, name) || []
            : options.find((option) => option.value === get(formData, name)) ||
              null
        }
        onChange={(event, newValue: any) => {
          const normalize = (items: any) => {
            // loại bỏ các phần tử trùng lặp trong mảng
            // nếu multiple là true thì items là mảng các phần tử, nếu không thì items là 1 phần tử duy nhất
            const deduped = multiple
              ? Array.from(
                  new Map(items.map((item) => [item.value, item])).values()
                )
              : items;

            const values = multiple ? deduped : deduped?.value;

            const labels = multiple
              ? deduped.map((item) => item?.label)
              : deduped?.label;

            return { values, labels };
          };

          const { values, labels } = normalize(newValue);

          onChange?.(name)(values);
          !!labelName && onChange?.(labelName)(labels);
          onKeyDown?.(name);
        }}
        disabled={disabled}
        popupIcon={
          <Box sx={{ padding: pxToRem(10) }}>
            <IconChevronDown />
          </Box>
        }
        onInputChange={(event, newInputValue) => onInputChange?.(newInputValue)}
        renderInput={(params) => (
          <TextField
            {...params}
            error={!!formErrors?.[name]}
            placeholder={
              multiple &&
              Array.isArray(get(formData, name)) &&
              get(formData, name).length > 0
                ? ""
                : placeholder
            }
            variant="standard"
            fullWidth
            inputRef={(el) => {
              inputRef.current = el;
              attachRef?.(name)?.(el);
            }}
            sx={{
              zIndex: 1,
            }}
            InputProps={{
              ...params.InputProps,
            }}
          />
        )}
        renderTags={(value, getTagProps) => {
          const typingValue = inputRef.current?.value ?? "";

          if (typingValue) return null; // đang gõ → ẩn tags
          const isAll = value?.length === options?.length;
          const labels = value?.map((option) => option?.label)?.join(", ");
          return (
            <Typography
              style={{
                fontSize: "16px",
                width: "calc(100% - 60px)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                position: "absolute",
                zIndex: -1,
                paddingLeft: 7,
              }}
              variant="body1"
            >
              {isAll ? "Tất cả" : labels}{" "}
            </Typography>
          );
        }}
        {...selectProps}
      />
      <Collapse in={!!get(formErrors, name)} timeout="auto">
        <FormHelperText error>{get(formErrors, name)}</FormHelperText>
      </Collapse>
    </Wrapper>
  );
};

export default FormSelect;
